import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ArrowLeft, Calendar, Phone, Mail, Building2, Clock, Check, X, MessageCircle, 
  Plus, FileText, CalendarClock, ThumbsDown, ShoppingCart
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CRMAppointments = ({ user, onLogout }) => {
  const navigate = useNavigate();
  
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [dateFilter, setDateFilter] = useState('');
  const [programs, setPrograms] = useState([]);
  
  // Communication dialog (for completing appointment with new communication)
  const [commDialogOpen, setCommDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [commForm, setCommForm] = useState({
    communication_type: 'call',
    outcome: '',
    notes: '',
    next_contact_date: '',
    program_interest: ''
  });

  // View previous communications dialog
  const [viewCommDialogOpen, setViewCommDialogOpen] = useState(false);
  const [viewingCommunications, setViewingCommunications] = useState([]);
  const [viewingCustomerName, setViewingCustomerName] = useState('');

  // Postpone dialog
  const [postponeDialogOpen, setPostponeDialogOpen] = useState(false);
  const [postponeAppointment, setPostponeAppointment] = useState(null);
  const [postponeDate, setPostponeDate] = useState('');

  // Not interested dialog
  const [notInterestedDialogOpen, setNotInterestedDialogOpen] = useState(false);
  const [notInterestedAppointment, setNotInterestedAppointment] = useState(null);
  const [notInterestedReason, setNotInterestedReason] = useState('');
  const [notInterestedProgramId, setNotInterestedProgramId] = useState('');

  // Complete sale dialog
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [completeAppointment, setCompleteAppointment] = useState(null);
  const [completeProgramId, setCompleteProgramId] = useState('');
  const [completeNotes, setCompleteNotes] = useState('');

  // Customer programs cache (for the dialog dropdowns)
  const [customerPrograms, setCustomerPrograms] = useState([]);
  const [loadingCustomerPrograms, setLoadingCustomerPrograms] = useState(false);

  useEffect(() => {
    fetchAppointments();
    fetchPrograms();
  }, [statusFilter, dateFilter]);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/crm/appointments?user_id=${user.user_id}&status=${statusFilter}`);
      setAppointments(response.data);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης ραντεβού');
    } finally {
      setLoading(false);
    }
  };

  const fetchPrograms = async () => {
    try {
      const response = await axios.get(`${API}/crm/programs?user_id=${user.user_id}`);
      setPrograms(response.data);
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  };

  const handleUpdateStatus = async (appointmentId, newStatus) => {
    try {
      await axios.put(`${API}/crm/appointments/${appointmentId}?user_id=${user.user_id}`, {
        status: newStatus
      });
      toast.success(newStatus === 'completed' ? 'Η πώληση ολοκληρώθηκε' : 'Το ραντεβού ακυρώθηκε');
      fetchAppointments();
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης');
    }
  };

  // View previous communications
  const handleViewCommunications = async (appointment) => {
    try {
      const response = await axios.get(`${API}/crm/customers/${appointment.customer_id}/communications?user_id=${user.user_id}`);
      setViewingCommunications(response.data);
      setViewingCustomerName(appointment.customer_name);
      setViewCommDialogOpen(true);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης επικοινωνιών');
    }
  };

  // Postpone appointment
  const openPostponeDialog = (appointment) => {
    setPostponeAppointment(appointment);
    setPostponeDate('');
    setPostponeDialogOpen(true);
  };

  const handlePostpone = async () => {
    if (!postponeDate) {
      toast.error('Επιλέξτε νέα ημερομηνία');
      return;
    }
    
    try {
      await axios.put(`${API}/crm/appointments/${postponeAppointment.appointment_id}?user_id=${user.user_id}`, {
        scheduled_date: postponeDate,
        notes: `${postponeAppointment.notes || ''}\n[Αναβολή από ${formatDate(postponeAppointment.scheduled_date)}]`.trim()
      });
      toast.success('Το ραντεβού αναβλήθηκε');
      setPostponeDialogOpen(false);
      setPostponeAppointment(null);
      fetchAppointments();
    } catch (error) {
      toast.error('Σφάλμα αναβολής');
    }
  };

  // Fetch programs available for this appointment context.
  // Merges customer.program_interests + appointment.program_interests (deduped).
  const fetchCustomerPrograms = async (customerId, appointment = null) => {
    setLoadingCustomerPrograms(true);
    setCustomerPrograms([]);
    try {
      const resp = await axios.get(`${API}/crm/customers/${customerId}?user_id=${user.user_id}`);
      const cust = resp.data || {};
      const custIds = cust.program_interests || [];
      const custNames = cust.program_names || [];
      
      // Build customer pairs (skip orphans = empty names)
      const pairs = custIds
        .map((id, idx) => ({ id, name: custNames[idx] || '' }))
        .filter(p => p.name && p.name.trim() !== '');
      
      // Merge in appointment's own program_interests (if any) using the master programs list for names
      const aptProgIds = appointment?.program_interests || [];
      const existingIds = new Set(pairs.map(p => p.id));
      for (const pid of aptProgIds) {
        if (existingIds.has(pid)) continue;
        const prog = programs.find(pp => pp.program_id === pid);
        if (prog && prog.name) {
          pairs.push({ id: pid, name: prog.name });
          existingIds.add(pid);
        }
      }
      
      setCustomerPrograms(pairs);
    } catch (e) {
      console.error('Error fetching customer programs:', e);
      toast.error('Σφάλμα φόρτωσης προγραμμάτων πελάτη');
    } finally {
      setLoadingCustomerPrograms(false);
    }
  };

  const openNotInterestedDialog = (appointment) => {
    setNotInterestedAppointment(appointment);
    setNotInterestedReason('');
    setNotInterestedProgramId('');
    setNotInterestedDialogOpen(true);
    fetchCustomerPrograms(appointment.customer_id, appointment);
  };

  const handleNotInterested = async () => {
    if (!notInterestedReason.trim()) {
      toast.error('Συμπληρώστε τον λόγο');
      return;
    }
    
    try {
      // Create communication with "not interested" outcome (auto-removes program from customer if provided)
      const commPayload = {
        customer_id: notInterestedAppointment.customer_id,
        communication_type: 'call',
        outcome: 'Δεν ενδιαφέρεται',
        notes: notInterestedReason,
        interest_status: 'not_interested'
      };
      if (notInterestedProgramId) {
        commPayload.program_interest = notInterestedProgramId;
      }
      await axios.post(`${API}/crm/communications?user_id=${user.user_id}`, commPayload);
      
      // Cancel the appointment
      await axios.put(`${API}/crm/appointments/${notInterestedAppointment.appointment_id}?user_id=${user.user_id}`, {
        status: 'cancelled',
        notes: `${notInterestedAppointment.notes || ''}\n[Δεν ενδιαφέρεται: ${notInterestedReason}]`.trim()
      });
      
      toast.success(notInterestedProgramId 
        ? 'Καταχωρήθηκε ως "Δεν ενδιαφέρεται" και το πρόγραμμα αφαιρέθηκε'
        : 'Καταχωρήθηκε ως "Δεν ενδιαφέρεται"');
      setNotInterestedDialogOpen(false);
      setNotInterestedAppointment(null);
      fetchAppointments();
    } catch (error) {
      toast.error('Σφάλμα καταχώρησης');
    }
  };

  // Complete sale
  const openCompleteSaleDialog = (appointment) => {
    setCompleteAppointment(appointment);
    setCompleteProgramId('');
    setCompleteNotes('');
    setCompleteDialogOpen(true);
    fetchCustomerPrograms(appointment.customer_id, appointment);
  };

  const handleCompleteSale = async () => {
    try {
      // Create communication with "completed" outcome (auto-removes program from customer if provided)
      const commPayload = {
        customer_id: completeAppointment.customer_id,
        communication_type: 'call',
        outcome: 'Ολοκλήρωση Πώλησης',
        notes: completeNotes || '',
        interest_status: 'completed'
      };
      if (completeProgramId) {
        commPayload.program_interest = completeProgramId;
      }
      await axios.post(`${API}/crm/communications?user_id=${user.user_id}`, commPayload);
      
      // Mark appointment as completed
      await axios.put(`${API}/crm/appointments/${completeAppointment.appointment_id}?user_id=${user.user_id}`, {
        status: 'completed'
      });
      
      toast.success(completeProgramId
        ? 'Η πώληση ολοκληρώθηκε και το πρόγραμμα αφαιρέθηκε'
        : 'Η πώληση ολοκληρώθηκε');
      setCompleteDialogOpen(false);
      setCompleteAppointment(null);
      fetchAppointments();
    } catch (error) {
      toast.error('Σφάλμα ολοκλήρωσης');
    }
  };

  const openCommunicationDialog = (appointment) => {
    setSelectedAppointment(appointment);
    setCommForm({
      communication_type: 'call',
      outcome: '',
      notes: appointment.notes || '',
      next_contact_date: '',
      program_interest: ''
    });
    setCommDialogOpen(true);
  };

  const handleCreateCommunication = async (e) => {
    e.preventDefault();
    
    if (!commForm.outcome.trim()) {
      toast.error('Το αποτέλεσμα είναι υποχρεωτικό');
      return;
    }
    
    try {
      // Create communication with program interest
      const commData = {
        customer_id: selectedAppointment.customer_id,
        communication_type: commForm.communication_type,
        outcome: commForm.outcome,
        notes: commForm.notes,
        next_contact_date: commForm.next_contact_date || null,
        program_interest: commForm.program_interest || null
      };
      
      await axios.post(`${API}/crm/communications?user_id=${user.user_id}`, commData);
      
      // Mark appointment as completed
      await axios.put(`${API}/crm/appointments/${selectedAppointment.appointment_id}?user_id=${user.user_id}`, {
        status: 'completed'
      });
      
      toast.success('Η επικοινωνία καταχωρήθηκε και το ραντεβού ολοκληρώθηκε' + 
        (commForm.next_contact_date ? ' - Δημιουργήθηκε νέο ραντεβού' : ''));
      
      setCommDialogOpen(false);
      setSelectedAppointment(null);
      fetchAppointments();
    } catch (error) {
      toast.error('Σφάλμα καταχώρησης');
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('el-GR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('el-GR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleTimeString('el-GR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '';
    }
  };

  const isOverdue = (dateStr) => {
    if (!dateStr) return false;
    try {
      return new Date(dateStr) < new Date();
    } catch {
      return false;
    }
  };

  // Filter appointments by date
  const filteredAppointments = dateFilter
    ? appointments.filter(apt => apt.scheduled_date?.startsWith(dateFilter))
    : appointments;

  const groupAppointmentsByDate = () => {
    const groups = {};
    filteredAppointments.forEach(apt => {
      const date = apt.scheduled_date?.split('T')[0] || 'unknown';
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(apt);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                onClick={() => navigate('/crm')}
                data-testid="back-button"
                className="shrink-0"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Πίσω
              </Button>
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-slate-400" />
                  Οι Πωλήσεις Μου - Ραντεβού
                </h1>
                <p className="text-xs sm:text-sm text-slate-600">
                  {user.role === 'owner' ? 'Όλα τα ραντεβού της ομάδας' : 'Τα δικά σου ραντεβού'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-36"
                data-testid="date-filter"
              />
              {dateFilter && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDateFilter('')}
                  className="h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36" data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Εκκρεμή</SelectItem>
                  <SelectItem value="completed">Ολοκληρωμένα</SelectItem>
                  <SelectItem value="cancelled">Ακυρωμένα</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-12 text-slate-500">Φόρτωση...</div>
        ) : filteredAppointments.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
            <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">
              {statusFilter === 'pending' ? 'Δεν υπάρχουν εκκρεμή ραντεβού' :
               statusFilter === 'completed' ? 'Δεν υπάρχουν ολοκληρωμένα ραντεβού' :
               'Δεν υπάρχουν ακυρωμένα ραντεβού'}
            </h3>
            <p className="text-slate-500 text-sm">
              Τα ραντεβού δημιουργούνται αυτόματα όταν καταχωρείτε επικοινωνία με ημερομηνία επόμενης επαφής
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {groupAppointmentsByDate().map(([date, apts]) => (
              <div key={date}>
                <h3 className="text-sm font-semibold text-slate-500 mb-3 uppercase tracking-wide">
                  {formatDate(date)}
                </h3>
                <div className="space-y-3">
                  {apts.map(apt => (
                    <div
                      key={apt.appointment_id}
                      className={`bg-white rounded-lg shadow-sm border p-4 ${
                        statusFilter === 'pending' && isOverdue(apt.scheduled_date) 
                          ? 'border-red-300 bg-red-50' 
                          : ''
                      }`}
                      data-testid={`appointment-card-${apt.appointment_id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          {/* Time and Status */}
                          <div className="flex items-center gap-2 mb-2">
                            <Clock className="h-4 w-4 text-slate-400" />
                            <span className="font-medium text-slate-900">
                              {formatTime(apt.scheduled_date)}
                            </span>
                            {statusFilter === 'pending' && isOverdue(apt.scheduled_date) && (
                              <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                                Εκπρόθεσμο
                              </span>
                            )}
                          </div>
                          
                          {/* Customer Info */}
                          <div 
                            className="cursor-pointer hover:text-blue-600"
                            onClick={() => navigate(`/crm/customers/${apt.customer_id}`)}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <Building2 className="h-4 w-4 text-slate-400" />
                              <span className="font-semibold text-slate-900">
                                {apt.customer_name || 'Άγνωστος πελάτης'}
                              </span>
                            </div>
                          </div>
                          
                          {/* Contact Info */}
                          <div className="flex flex-wrap gap-4 text-sm text-slate-600 mt-2">
                            {apt.customer_mobile && (
                              <a href={`tel:${apt.customer_mobile}`} className="flex items-center gap-1 hover:text-blue-600">
                                <Phone className="h-3 w-3" />
                                {apt.customer_mobile}
                              </a>
                            )}
                            {apt.customer_phone && !apt.customer_mobile && (
                              <a href={`tel:${apt.customer_phone}`} className="flex items-center gap-1 hover:text-blue-600">
                                <Phone className="h-3 w-3" />
                                {apt.customer_phone}
                              </a>
                            )}
                            {apt.customer_email && (
                              <a href={`mailto:${apt.customer_email}`} className="flex items-center gap-1 hover:text-blue-600">
                                <Mail className="h-3 w-3" />
                                {apt.customer_email}
                              </a>
                            )}
                          </div>
                          
                          {/* Notes */}
                          {apt.notes && (
                            <div className="mt-2 p-2 bg-slate-50 rounded text-sm text-slate-600">
                              {apt.notes}
                            </div>
                          )}
                          
                          {/* Programs of Interest */}
                          {apt.program_interests?.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {apt.program_interests.map(pid => {
                                const prog = programs.find(p => p.program_id === pid);
                                return prog ? (
                                  <span key={pid} className="inline-flex items-center px-2 py-0.5 rounded bg-purple-50 text-purple-700 text-xs font-medium">
                                    {prog.name}
                                  </span>
                                ) : null;
                              })}
                            </div>
                          )}
                          
                          {/* Completed date */}
                          {apt.completed_at && (
                            <div className="mt-2 text-xs text-green-600 font-medium">
                              Ολοκλήρωση: {formatDateTime(apt.completed_at)}
                            </div>
                          )}
                          
                          {/* Employee Info */}
                          <div className="mt-2 text-xs text-slate-500">
                            Υπεύθυνος: {apt.employee_name}
                          </div>
                        </div>
                        
                        {/* Actions */}
                        {statusFilter === 'pending' && (
                          <div className="flex flex-col gap-2 ml-4">
                            {/* View Communications */}
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                              onClick={() => handleViewCommunications(apt)}
                              data-testid={`view-comm-${apt.appointment_id}`}
                            >
                              <FileText className="h-4 w-4 mr-1" />
                              Ιστορικό
                            </Button>
                            
                            {/* Log Communication */}
                            <Button
                              size="sm"
                              onClick={() => openCommunicationDialog(apt)}
                              data-testid={`log-communication-${apt.appointment_id}`}
                            >
                              <MessageCircle className="h-4 w-4 mr-1" />
                              Καταχώρηση
                            </Button>
                            
                            {/* Postpone */}
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-amber-600 border-amber-200 hover:bg-amber-50"
                              onClick={() => openPostponeDialog(apt)}
                              data-testid={`postpone-${apt.appointment_id}`}
                            >
                              <CalendarClock className="h-4 w-4 mr-1" />
                              Αναβολή
                            </Button>
                            
                            {/* Complete Sale */}
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 border-green-600 hover:bg-green-50"
                              onClick={() => openCompleteSaleDialog(apt)}
                              data-testid={`complete-${apt.appointment_id}`}
                            >
                              <ShoppingCart className="h-4 w-4 mr-1" />
                              Ολοκλήρωση Πώλησης
                            </Button>
                            
                            {/* Not Interested */}
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-600 hover:bg-red-50"
                              onClick={() => openNotInterestedDialog(apt)}
                              data-testid={`not-interested-${apt.appointment_id}`}
                            >
                              <ThumbsDown className="h-4 w-4 mr-1" />
                              Δεν Ενδιαφέρεται
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Communication Dialog */}
      <Dialog open={commDialogOpen} onOpenChange={setCommDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Καταχώρηση Επικοινωνίας
              {selectedAppointment && (
                <span className="block text-sm font-normal text-slate-600 mt-1">
                  {selectedAppointment.customer_name}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateCommunication} className="space-y-4">
            <div>
              <Label>Τύπος Επικοινωνίας</Label>
              <Select
                value={commForm.communication_type}
                onValueChange={(v) => setCommForm(prev => ({ ...prev, communication_type: v }))}
              >
                <SelectTrigger data-testid="comm-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">Τηλεφωνική Κλήση</SelectItem>
                  <SelectItem value="meeting">Συνάντηση</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Πρόγραμμα Ενδιαφέροντος</Label>
              <Select
                value={commForm.program_interest}
                onValueChange={(v) => setCommForm(prev => ({ ...prev, program_interest: v === 'none' ? '' : v }))}
              >
                <SelectTrigger data-testid="comm-program-select">
                  <SelectValue placeholder="Επιλέξτε πρόγραμμα..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Κανένα</SelectItem>
                  {programs.map(prog => (
                    <SelectItem key={prog.program_id} value={prog.program_id}>
                      {prog.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Αποτέλεσμα *</Label>
              <Input
                value={commForm.outcome}
                onChange={(e) => setCommForm(prev => ({ ...prev, outcome: e.target.value }))}
                placeholder="π.χ. Ενδιαφέρεται, θέλει προσφορά"
                required
                data-testid="comm-outcome-input"
              />
            </div>
            
            <div>
              <Label>Σημειώσεις</Label>
              <Textarea
                value={commForm.notes}
                onChange={(e) => setCommForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                data-testid="comm-notes-input"
              />
            </div>
            
            <div>
              <Label>Επόμενη Επικοινωνία</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="date"
                  value={commForm.next_contact_date?.split('T')[0] || ''}
                  onChange={(e) => {
                    const date = e.target.value;
                    const existing = commForm.next_contact_date?.split('T')[1] || '10:00';
                    setCommForm(prev => ({ ...prev, next_contact_date: date ? `${date}T${existing}` : '' }));
                  }}
                  data-testid="comm-next-date-input"
                />
                <Input
                  type="time"
                  value={commForm.next_contact_date?.split('T')[1]?.slice(0,5) || ''}
                  onChange={(e) => {
                    const time = e.target.value || '10:00';
                    const existing = commForm.next_contact_date?.split('T')[0];
                    if (existing) {
                      setCommForm(prev => ({ ...prev, next_contact_date: `${existing}T${time}` }));
                    }
                  }}
                  data-testid="comm-next-time-input"
                  disabled={!commForm.next_contact_date?.split('T')[0]}
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Αν συμπληρωθεί ημερομηνία, θα δημιουργηθεί νέο ραντεβού (default ώρα 10:00)
              </p>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setCommDialogOpen(false)}>
                Ακύρωση
              </Button>
              <Button type="submit" data-testid="save-communication-button">
                Καταχώρηση & Ολοκλήρωση
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Communications Dialog */}
      <Dialog open={viewCommDialogOpen} onOpenChange={setViewCommDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Ιστορικό Επικοινωνίας
              <span className="block text-sm font-normal text-slate-600 mt-1">
                {viewingCustomerName}
              </span>
            </DialogTitle>
          </DialogHeader>
          
          {viewingCommunications.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              Δεν υπάρχουν προηγούμενες επικοινωνίες
            </div>
          ) : (
            <div className="space-y-4">
              {viewingCommunications.map((comm, index) => (
                <div key={comm.communication_id || index} className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      comm.communication_type === 'call' ? 'bg-blue-100 text-blue-700' :
                      comm.communication_type === 'meeting' ? 'bg-purple-100 text-purple-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {comm.communication_type === 'call' ? 'Κλήση' :
                       comm.communication_type === 'meeting' ? 'Συνάντηση' : 'Email'}
                    </span>
                    <span className="text-xs text-slate-500">
                      {formatDateTime(comm.created_at)}
                    </span>
                  </div>
                  <div className="font-medium text-slate-900 mb-1">
                    {comm.outcome}
                  </div>
                  {comm.notes && (
                    <div className="text-sm text-slate-600 bg-slate-50 rounded p-2">
                      {comm.notes}
                    </div>
                  )}
                  {comm.estimated_budget && (
                    <div className="text-xs text-slate-500 mt-1">
                      Προϋπολογισμός: {comm.estimated_budget.toLocaleString('el-GR')} €
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Postpone Dialog */}
      <Dialog open={postponeDialogOpen} onOpenChange={setPostponeDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Αναβολή Ραντεβού</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Νέα Ημερομηνία & Ώρα *</Label>
              <Input
                type="datetime-local"
                value={postponeDate}
                onChange={(e) => setPostponeDate(e.target.value)}
                data-testid="postpone-date-input"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setPostponeDialogOpen(false)}>
                Ακύρωση
              </Button>
              <Button onClick={handlePostpone} data-testid="confirm-postpone-btn">
                Αναβολή
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Not Interested Dialog */}
      <Dialog open={notInterestedDialogOpen} onOpenChange={setNotInterestedDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Δεν Ενδιαφέρεται</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Πρόγραμμα (προαιρετικό)</Label>
              {loadingCustomerPrograms ? (
                <div className="text-sm text-slate-500 py-2">Φόρτωση...</div>
              ) : customerPrograms.length === 0 ? (
                <div className="text-sm text-slate-600 py-2 px-3 bg-slate-50 border border-slate-200 rounded">
                  Δεν υπάρχουν προγράμματα συσχετισμένα. Μπορείτε να προχωρήσετε χωρίς επιλογή.
                </div>
              ) : (
                <Select
                  value={notInterestedProgramId}
                  onValueChange={setNotInterestedProgramId}
                >
                  <SelectTrigger data-testid="not-interested-program-select">
                    <SelectValue placeholder="Επιλέξτε πρόγραμμα (ή αφήστε κενό)" />
                  </SelectTrigger>
                  <SelectContent>
                    {customerPrograms.map(p => (
                      <SelectItem key={p.id} value={p.id} data-testid={`not-interested-prog-${p.id}`}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <p className="text-xs text-slate-500 mt-1">
                Αν επιλέξετε πρόγραμμα, θα αφαιρεθεί από τα προγράμματα ενδιαφέροντος του πελάτη.
              </p>
            </div>

            <div>
              <Label>Λόγος *</Label>
              <Textarea
                value={notInterestedReason}
                onChange={(e) => setNotInterestedReason(e.target.value)}
                placeholder="π.χ. Δεν έχει budget, Έχει άλλες προτεραιότητες, κτλ."
                rows={3}
                data-testid="not-interested-reason"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setNotInterestedDialogOpen(false)}>
                Ακύρωση
              </Button>
              <Button 
                onClick={handleNotInterested} 
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-not-interested-btn"
              >
                Καταχώρηση
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Complete Sale Dialog */}
      <Dialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ολοκλήρωση Πώλησης</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Πρόγραμμα (προαιρετικό)</Label>
              {loadingCustomerPrograms ? (
                <div className="text-sm text-slate-500 py-2">Φόρτωση...</div>
              ) : customerPrograms.length === 0 ? (
                <div className="text-sm text-slate-600 py-2 px-3 bg-slate-50 border border-slate-200 rounded">
                  Δεν υπάρχουν προγράμματα συσχετισμένα. Μπορείτε να προχωρήσετε χωρίς επιλογή.
                </div>
              ) : (
                <Select
                  value={completeProgramId}
                  onValueChange={setCompleteProgramId}
                >
                  <SelectTrigger data-testid="complete-program-select">
                    <SelectValue placeholder="Επιλέξτε πρόγραμμα (ή αφήστε κενό)" />
                  </SelectTrigger>
                  <SelectContent>
                    {customerPrograms.map(p => (
                      <SelectItem key={p.id} value={p.id} data-testid={`complete-prog-${p.id}`}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <p className="text-xs text-slate-500 mt-1">
                Αν επιλέξετε πρόγραμμα, θα αφαιρεθεί από τα προγράμματα ενδιαφέροντος του πελάτη.
              </p>
            </div>

            <div>
              <Label>Σημειώσεις (προαιρετικό)</Label>
              <Textarea
                value={completeNotes}
                onChange={(e) => setCompleteNotes(e.target.value)}
                placeholder="π.χ. Υπογραφή σύμβασης, λεπτομέρειες πώλησης..."
                rows={3}
                data-testid="complete-notes"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setCompleteDialogOpen(false)}>
                Ακύρωση
              </Button>
              <Button 
                onClick={handleCompleteSale} 
                className="bg-green-600 hover:bg-green-700"
                data-testid="confirm-complete-sale-btn"
              >
                Ολοκλήρωση
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CRMAppointments;
